import { useReducer } from 'react'
import { SearchFilteredRecipesInterface, SearchFiltersInterface, SearchIngredients } from '../app/search/search.types'
import { getServiceSearchResults, filterStringHandler } from '../app/search/search.utils'
import { RootState } from '../app/store/store';
import { useAppSelector } from '../app/store/hooks';
import { userSelector } from '../app/main/mainSlice';
import { UseGetFlag } from '../app/launchDarkly';


interface SearchState {
    searchString: string;
    localSelected: {
        cuisineFilter: Array<string>,
        ingredientFilter: Array<string>,
        courseFilter: string,
        timeFilter: string,
        servingSizeFilter: Array<string>,
        mealTypeFilter: Array<string>,
    }
    drawerOpen: boolean,
    searchFilterLoading: boolean;
    searchFilters: SearchFiltersInterface;
    recipeList: Array<SearchFilteredRecipesInterface>
    searchFilteredIngredients: Array<SearchIngredients>
}

interface Action {
    type: string;
    payload?: any;
}

interface Filters {
    cuisineFilter?: string[],
    ingredientFilter?: string[],
    courseFilter?: string,
    timeFilter?: string,
    servingSizeFilter?: string[],
    mealTypeFilter?: string[],
}

const INITIAL_FILTER_STATE = {
    cuisineFilter: [],
    ingredientFilter: [],
    courseFilter: '',
    timeFilter: '',
    servingSizeFilter: [],
    mealTypeFilter: [],
}

const initialState = {
    drawerOpen: false,
    searchString: '',
    searchFilterLoading: false,
    localSelected: INITIAL_FILTER_STATE,
    searchFilters: INITIAL_FILTER_STATE,
    recipeList: [],
    searchFilteredIngredients: [],
};

const searchReducer = (state: SearchState, action: Action) => {
    switch (action.type) {
        case 'SET_SEARCH_STRING':
            return {
                ...state,
                searchString: action.payload,
            };
        case 'SET_LOCAL_SEARCH_FILTERS':
            return {
                ...state,
                localSelected: { ...state.localSelected, ...action.payload },
            };

        case 'SET_LOCAL_CUISINE':
            return {
                ...state,
                localSelected: {
                    ...state.localSelected,
                    cuisineFilter: action.payload
                }
            }

        case 'SET_LOCAL_INGREDIENT':
            return {
                ...state,
                localSelected: {
                    ...state.localSelected,
                    ingredientFilter: action.payload
                }
            }

        case 'SET_LOCAL_COURSE':
            return {
                ...state,
                localSelected: {
                    ...state.localSelected,
                    courseFilter: action.payload
                }
            }

        case 'SET_LOCAL_TIME':
            return {
                ...state,
                localSelected: {
                    ...state.localSelected,
                    timeFilter: action.payload
                }
            }

        case 'SET_LOCAL_SERVING_SIZE':
            return {
                ...state,
                localSelected: {
                    ...state.localSelected,
                    servingSizeFilter: action.payload
                }
            }

        case 'SET_LOCAL_MEAL_TYPE':
            return {
                ...state,
                localSelected: {
                    ...state.localSelected,
                    mealTypeFilter: action.payload
                }
            }

        case 'RESET_LOCAL_SELECTED':
            return {
                ...state,
                localSelected: {
                    cuisineFilter: [],
                    ingredientFilter: [],
                    courseFilter: '',
                    timeFilter: '',
                    servingSizeFilter: [],
                    mealTypeFilter: [],
                }
            }

        case 'TOGGLE_DRAWER':
            return {
                ...state,
                drawerOpen: !state.drawerOpen,
            };

        case 'SET_DRAWER_OPEN':
            return {
                ...state,
                drawerOpen: action.payload,
            };

        case 'SET_SEARCH_QUERY_LOADING':
            return {
                ...state,
                searchFilterLoading: action.payload,
            };

        case 'SET_SEARCH_FILTERS':
            return {
                ...state,
                searchFilters: { ...state.searchFilters, ...action.payload },
            };

        case 'CLEAR_SEARCH_FILTERS':
            return {
                ...state,
                searchFilters: {
                    cuisineFilter: [],
                    ingredientFilter: [],
                    courseFilter: '',
                    timeFilter: '',
                    servingSizeFilter: [],
                    mealTypeFilter: [],
                },
            };

        case 'SET_RECIPE_LIST':
            return {
                ...state,
                recipeList: action.payload,
            };

        case 'SET_FILTERED_INGREDIENTS':
            return {
                ...state,
                searchFilteredIngredients: action.payload,
            };

        case 'CLEAR_RECIPES':
            return {
                ...state,
                recipeList: [],
            };

        default:
            return state;
    }
};

const getServiceFiltersSearchList = async ({ queryString, filterString }: { queryString: string, filterString: string }) => {
    const serviceSearchResult = await getServiceSearchResults(queryString, filterString)
    return serviceSearchResult
}

const getServiceQuerySearchList = async ({ queryString, filterString }: { queryString: string, filterString: string }) => {
    const serviceSearchResult = await getServiceSearchResults(queryString, filterString)
    return { algoliaResults: serviceSearchResult }
}

const useSearch = () => {
    const [state, dispatch] = useReducer(searchReducer, initialState);
    const user = useAppSelector(userSelector)
    const { enableAdminFeatures } = UseGetFlag()

    function handleDrawerClose() {
        dispatch({ type: 'SET_DRAWER_OPEN', payload: false });
        dispatch({ type: 'RESET_LOCAL_SELECTED' });

    }

    function setSearchString(queryString: string) {
        dispatch({ type: 'SET_SEARCH_STRING', payload: queryString });
    }

    function handleDrawerOpen() {
        dispatch({ type: 'SET_DRAWER_OPEN', payload: true });
    }


    const clearFilter = async () => {
        dispatch({ type: 'SET_SEARCH_QUERY_LOADING', payload: true });

        dispatch({ type: 'CLEAR_SEARCH_FILTERS' });
        dispatch({ type: 'RESET_LOCAL_SELECTED' });
        await setRecipeList(state.searchString, INITIAL_FILTER_STATE);
        dispatch({ type: 'SET_SEARCH_QUERY_LOADING', payload: false });

        handleDrawerClose()

    }

    // do we need to consider the query string while applying filters
    const applySearchFilters = async () => {
        dispatch({ type: 'SET_SEARCH_QUERY_LOADING', payload: true });
        const { localSelected } = state
        const filterString = filterStringHandler(user, localSelected, enableAdminFeatures)
        const algoliaResults = await getServiceFiltersSearchList({ queryString: state.searchString ?? "", filterString })
        dispatch({ type: 'SET_RECIPE_LIST', payload: algoliaResults ?? [] });
        dispatch({ type: 'SET_SEARCH_FILTERS', payload: state.localSelected });
        dispatch({ type: 'SET_SEARCH_QUERY_LOADING', payload: false });

        handleDrawerClose()

    }

    const applySearchFiltersToLocal = async () => {
        dispatch({ type: 'SET_LOCAL_SEARCH_FILTERS', payload: state.searchFilters });

    }

    // do not need queryString and filters as it can be accessed from reducer
    const setRecipeList = async (queryString: string, filters?: Filters) => {
        const searchFilters = filters ?? state.searchFilters;
        const filterString = filterStringHandler(user, searchFilters, enableAdminFeatures)
        const recipeList = await getServiceQuerySearchList({ queryString, filterString });
        dispatch({ type: 'SET_RECIPE_LIST', payload: recipeList?.algoliaResults ?? [] });

    }

    const setLocalCuisine = (cuisine: Array<string>) => {
        dispatch({ type: 'SET_LOCAL_CUISINE', payload: cuisine });
    };

    const setLocalIngredient = (ingredients: Array<string>) => {
        dispatch({ type: 'SET_LOCAL_INGREDIENT', payload: ingredients });
    };

    const setLocalCourse = (course: string) => {
        dispatch({ type: 'SET_LOCAL_COURSE', payload: course });
    };

    const setLocalTime = (time: string) => {
        dispatch({ type: 'SET_LOCAL_TIME', payload: time });
    };

    const setLocalServingSize = (servingSizes: Array<string>) => {
        dispatch({ type: 'SET_LOCAL_SERVING_SIZE', payload: servingSizes });
    };

    const setLocalMealType = (mealTypes: Array<string>) => {
        dispatch({ type: 'SET_LOCAL_MEAL_TYPE', payload: mealTypes });
    };

    return {
        searchFilterLoading: state.searchFilterLoading,
        localSelected: state.localSelected,
        searchFilters: state.searchFilters,
        recipeList: state.recipeList,
        searchQueryString: state.searchString,
        drawerOpen: state.drawerOpen,
        setRecipeList,
        handleDrawerClose,
        handleDrawerOpen,
        clearFilter,
        applySearchFilters,
        setLocalCuisine,
        setLocalIngredient,
        setLocalCourse,
        setLocalTime,
        setLocalServingSize,
        setLocalMealType,
        setSearchString,
        applySearchFiltersToLocal
    }

};

export default useSearch